import { Injectable } from '@angular/core';
import { UserService } from 'src/app/services/user.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { catchError, map, Observable, of, Subject } from 'rxjs';
import { LastPageService } from 'src/app/services/last-visited-page/last-visited-page.service';
import { NavAppService } from 'src/app/services/nav-app.service';
import { WebComponentDTO } from 'src/app/cc-api/models';

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  BASE_URL: string = '';
  sidebarLoadingComplete = new Subject<any[]>();

  constructor(
    private userService: UserService,
    private featureFlagsService: FeatureFlagsService,
    private lastPageService: LastPageService,
    private navAppService: NavAppService,
  ) {}

  getSidebarConfig(currentURL: string): Observable<any[]> {
    const sidebarItems = [];

    sidebarItems.push({
      title: 'Projects',
      id: 'PROJECTS',
      icon: { icon: 'three-layers-outline', pack: 'fci' },
      link:
        this.lastPageService?.getLastVisitedPage('STACK') ||
        this.BASE_URL + '/home',
      pathMatch: 'full',
      selected: this.isBlueprintMenuActive(currentURL),
    });

    sidebarItems.push({
      title: 'Audit Logs',
      id: 'AUDIT_LOGS',
      icon: { icon: 'list', pack: 'fci' },
      link: this.BASE_URL + '/central-audit-logs',
      pathMatch: 'full',
      selected: this.isAuditLogsMenuActive(currentURL),
    });

    sidebarItems.push({
      title: 'Ops Center',
      id: 'OPS_CENTER',
      icon: { icon: 'ops-center', pack: 'fci' },
      link: this.BASE_URL + '/ops-center',
      pathMatch: 'full',
      selected: this.isOpsCenterMenuActive(currentURL),
    });

    if (this.featureFlagsService.isMercedesCP) {
      sidebarItems.push({
        title: 'Software Engineering Guidelines',
        id: 'MERC_GUIDELINES',
        icon: { icon: 'book-open-outline', pack: 'fci' },
        link: '/software-engineering-guidelines',
        selected: this.isMercGuidelinesOpen(currentURL),
      });
    }

    const settingsSubMenu = this.userService.getSidebarSubMenuItemsOf(
      'SETTINGS',
      this.BASE_URL,
    );

    if (settingsSubMenu?.length) {
      sidebarItems.push({
        id: 'SETTINGS',
        title: 'Organization Settings',
        icon: { icon: 'settings-outline', pack: 'fci' },
        link: `${this.BASE_URL}/settings`,
        pathMatch: 'prefix',
        selected: this.isSettingsMenuActive(currentURL),
      });
    }

    if (this.featureFlagsService.showWebComponents()) {
      // Instead of returning a static array now, we will return an observable
      // which calls getNavigationConfigs and pipes the resulting observable
      // into a map function which transforms that data into our required
      // sidebar configuration.
      // This allows us to load and display the sidebar items in a lazy manner,
      // reducing the initial app load time.
      //
      // Note: This approach assumes that the navigation configurations are
      // fetched asynchronously and stored in the navAppService.
      // If your navigation configurations are static, you can directly return
      // the sidebarItems array.
      //
      // We also use the catchError operator to handle any errors that occur during
      // the fetching or mapping process. If an error occurs, the sidebar loading
      // complete subject will be notified with the current sidebarItems array.
      // This ends up returning only the static sidebar items
      return this.navAppService.getNavigationConfigs().pipe(
        map((configs: WebComponentDTO[]) => {
          configs?.forEach((config) => {
            if (config.enabled && config.type === 'NAV_APP') {
              sidebarItems.push({
                title: config.tooltip,
                id: config.name?.toUpperCase(),
                icon: config.iconURL,
                link: `${this.BASE_URL}/nav-app/${config.name}`,
                pathMatch: 'full',
                selected: this.isNavAppActive(currentURL, config.name),
              });
            }
          });
          return sidebarItems;
        }),
        catchError((error: any) => {
          this.sidebarLoadingComplete.next(sidebarItems);
          return of(sidebarItems);
        }),
      );
    } else {
      return of(sidebarItems);
    }
  }

  private isNavAppActive(currentURL: string, navAppName: string): boolean {
    const urlParams = currentURL?.split('/');
    return urlParams?.[1] === 'nav-app' && urlParams?.[2] === navAppName;
  }
  isSettingsMenuActive(currentURL): boolean {
    const urlParams = currentURL.split('/');
    if (urlParams?.[1] === 'settings') {
      return true;
    }
    return false;
  }

  isAuditLogsMenuActive(currentURL): boolean {
    const urlParams = currentURL.split('/');
    if (urlParams?.[1] === 'central-audit-logs') {
      return true;
    }
    return false;
  }

  isOpsCenterMenuActive(currentURL): boolean {
    const urlParams = currentURL.split('/');
    if (urlParams?.[1] === 'ops-center') {
      return true;
    }
    return false;
  }

  isBlueprintMenuActive(currentURL): boolean {
    const urlParams = currentURL?.split('/');
    if (
      ['home', 'stack', 'artifact-ci-details']?.includes(urlParams?.[1]) ||
      ['cluster']?.includes(urlParams?.[2])
    ) {
      return true;
    }
    return false;
  }

  isMercGuidelinesOpen(currentURL): boolean {
    const urlParams = currentURL?.split('/');
    if (urlParams?.[1] === 'software-engineering-guidelines') {
      return true;
    }
    return false;
  }
}
